/**
 * Custom hook for managing chat models
 * Handles model loading, selection, persistence, and validation
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ModelInfo } from '@gofranz/gpt-muse-common';
import { chatService } from '../services/chatService';
import { useRustyState } from '../state';
import type { ChatError } from '../types/chat';
import { isValidModel, getDefaultModel } from '../lib/chat-utils';

export function useChatModels() {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ChatError | null>(null);

  /**
   * Save model selection to global settings
   */
  const saveModelSelection = useCallback(async (modelId: string) => {
    try {
      await chatService.saveGlobalSettings({ selectedModel: modelId });
      console.log('💾 Saved model selection:', modelId);
    } catch (err) {
      console.error('Failed to save model selection:', err);
    }
  }, []);

  /**
   * Load saved model selection from global settings
   */
  const loadSavedModelSelection = useCallback(async (availableModels: ModelInfo[]) => {
    try {
      const globalSettings = await chatService.getGlobalSettings();

      if (globalSettings?.selectedModel && isValidModel(globalSettings.selectedModel, availableModels)) {
        setSelectedModel(globalSettings.selectedModel);
        console.log('📖 Loaded saved model selection:', globalSettings.selectedModel);
      } else if (availableModels.length > 0) {
        // Set default model if no saved selection or invalid selection
        const defaultModel = getDefaultModel(availableModels);
        setSelectedModel(defaultModel);
        await saveModelSelection(defaultModel);
        console.log('🎯 Set default model:', defaultModel);
      }
    } catch (err) {
      console.error('Failed to load saved model selection:', err);
      // Fallback to first available model
      if (availableModels.length > 0) {
        const defaultModel = getDefaultModel(availableModels);
        setSelectedModel(defaultModel);
      }
    }
  }, [saveModelSelection]);

  /**
   * Load available models from API
   */
  const loadModels = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const api = useRustyState.getState().api;
      const response = await api.listModels();
      setModels(response.models);

      // Load saved model preference or set default
      await loadSavedModelSelection(response.models);

      console.log('🤖 Loaded models:', response.models.length);
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to load models:', chatError);
    } finally {
      setIsLoading(false);
    }
  }, [loadSavedModelSelection]);

  /**
   * Select a model and save the preference
   */
  const selectModel = useCallback(async (modelId: string) => {
    try {
      setError(null);

      if (!isValidModel(modelId, models)) {
        throw new Error(`Model ${modelId} is not available`);
      }

      setSelectedModel(modelId);
      await saveModelSelection(modelId);

      console.log('🎯 Selected model:', modelId);
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to select model:', chatError);
    }
  }, [models, saveModelSelection]);

  /**
   * Validate if a model is available
   */
  const validateModel = useCallback((modelId: string): boolean => {
    return isValidModel(modelId, models);
  }, [models]);

  /**
   * Get model by ID
   */
  const getModelById = useCallback((modelId: string): ModelInfo | null => {
    return models.find(model => model.id === modelId) || null;
  }, [models]);

  /**
   * Get current selected model info
   */
  const getSelectedModelInfo = useCallback((): ModelInfo | null => {
    return getModelById(selectedModel);
  }, [getModelById, selectedModel]);

  /**
   * Get models by capability
   */
  const getModelsByCapability = useCallback((capability: string): ModelInfo[] => {
    return models.filter(model => 
      model.capabilities.includes(capability as any)
    );
  }, [models]);

  /**
   * Get free models
   */
  const getFreeModels = useCallback((): ModelInfo[] => {
    return models.filter(model => model.free_tier);
  }, [models]);

  /**
   * Get models sorted by price (ascending)
   */
  const getModelsSortedByPrice = useCallback((): ModelInfo[] => {
    return [...models].sort((a, b) => {
      const aCost = a.pricing.min_input_cost + a.pricing.min_output_cost;
      const bCost = b.pricing.min_input_cost + b.pricing.min_output_cost;
      return aCost - bCost;
    });
  }, [models]);

  /**
   * Refresh models list
   */
  const refreshModels = useCallback(async () => {
    await loadModels();
  }, [loadModels]);

  /**
   * Reset to default model
   */
  const resetToDefault = useCallback(async () => {
    if (models.length > 0) {
      const defaultModel = getDefaultModel(models);
      await selectModel(defaultModel);
    }
  }, [models, selectModel]);

  /**
   * Get model statistics
   */
  const getModelStats = useCallback(() => {
    const totalModels = models.length;
    const freeModels = getFreeModels().length;
    const visionModels = getModelsByCapability('Vision').length;
    const toolModels = getModelsByCapability('Tools').length;
    const providers = new Set(models.map(m => m.provider_count)).size;
    
    return {
      total: totalModels,
      free: freeModels,
      vision: visionModels,
      tools: toolModels,
      providers,
      hasModels: totalModels > 0,
    };
  }, [models, getFreeModels, getModelsByCapability]);

  // Load models on mount
  useEffect(() => {
    loadModels();
  }, [loadModels]);

  // Actions object for external use (memoized to prevent re-renders)
  const actions = useMemo(() => ({
    load: loadModels,
    select: selectModel,
    validate: validateModel,
    refresh: refreshModels,
    resetToDefault,
  }), [
    loadModels,
    selectModel,
    validateModel,
    refreshModels,
    resetToDefault,
  ]);

  // Memoize return value to prevent unnecessary re-renders in consumers
  const result = useMemo(() => ({
    models,
    selectedModel,
    isLoading,
    error,
    actions,
    getModelById,
    getSelectedModelInfo,
    getModelsByCapability,
    getFreeModels,
    getModelsSortedByPrice,
    getModelStats,
  }), [
    models,
    selectedModel,
    isLoading,
    error,
    actions,
    getModelById,
    getSelectedModelInfo,
    getModelsByCapability,
    getFreeModels,
    getModelsSortedByPrice,
    getModelStats,
  ]);

  return result;
}